const mongodbCheatsheet = {
  id: "mongodb",
  title: "MongoDB Cheatsheet",
  description:
    "A comprehensive guide to MongoDB including CRUD operations, queries, aggregation, and best practices",
  language: "javascript",
  category: "Database",
  level: "All Levels",
  sections: [
    {
      title: "Getting Started",
      items: [
        {
          title: "Basic Commands",
          content: "Essential MongoDB shell commands",
          code: `// Show all databases
show dbs

// Switch to a database (creates if not exists)
use mydatabase

// Show current database
db

// Show collections in current database
show collections

// Create collection
db.createCollection("users")

// Drop database
db.dropDatabase()

// Drop collection
db.users.drop()

// Show collection stats
db.users.stats()`,
          note: "Basic commands for database and collection management",
        },
        {
          title: "MongoDB Connection",
          content: "Connect to MongoDB using Node.js",
          code: `// Using MongoDB Native Driver
const MongoClient = require('mongodb').MongoClient;
const uri = "mongodb://localhost:27017/mydatabase";

MongoClient.connect(uri, { useNewUrlParser: true, useUnifiedTopology: true })
  .then(client => {
    const db = client.db("mydatabase");
    console.log("Connected to MongoDB");
  })
  .catch(err => console.error("Connection error:", err));

// Using Mongoose
const mongoose = require('mongoose');
mongoose.connect('mongodb://localhost:27017/mydatabase', {
  useNewUrlParser: true,
  useUnifiedTopology: true
})
.then(() => console.log('Connected to MongoDB'))
.catch(err => console.error('Connection error:', err));`,
          note: "Different ways to connect to MongoDB from Node.js applications",
        },
      ],
    },
    {
      title: "CRUD Operations",
      items: [
        {
          title: "Create (Insert)",
          content: "Insert documents into collections",
          code: `// Insert one document
db.users.insertOne({
  name: "John Doe",
  email: "john@example.com",
  age: 30
})

// Insert multiple documents
db.users.insertMany([
  { name: "Jane Doe", email: "jane@example.com", age: 25 },
  { name: "Bob Smith", email: "bob@example.com", age: 35 }
])

// Insert with options
db.users.insertOne(
  { name: "Alice", email: "alice@example.com" },
  { writeConcern: { w: "majority", wtimeout: 5000 } }
)`,
          note: "Methods to insert single or multiple documents into a collection",
        },
        {
          title: "Read (Query)",
          content: "Query documents from collections",
          code: `// Find all documents
db.users.find()

// Find with conditions
db.users.find({ age: { $gt: 25 } })

// Find one document
db.users.findOne({ email: "john@example.com" })

// Projection (select specific fields)
db.users.find({}, { name: 1, email: 1, _id: 0 })

// Query operators
db.users.find({
  age: { $gte: 25, $lte: 50 },    // Range query
  name: { $in: ["John", "Jane"] }, // In array
  email: { $exists: true }         // Field exists
})

// Logical operators
db.users.find({
  $or: [
    { age: { $lt: 25 } },
    { age: { $gt: 50 } }
  ]
})

// Regular expressions
db.users.find({ name: /^J/ })  // Names starting with J`,
          note: "Different ways to query and filter documents",
        },
        {
          title: "Update",
          content: "Update existing documents",
          code: `// Update one document
db.users.updateOne(
  { email: "john@example.com" },
  { $set: { age: 31 } }
)

// Update multiple documents
db.users.updateMany(
  { age: { $lt: 25 } },
  { $inc: { age: 1 } }
)

// Replace entire document
db.users.replaceOne(
  { _id: ObjectId("...") },
  { name: "New Name", age: 40 }
)

// Update operators
db.users.updateOne(
  { _id: ObjectId("...") },
  {
    $set: { status: "active" },    // Set value
    $inc: { age: 1 },              // Increment
    $push: { tags: "new-tag" },    // Add to array
    $pull: { tags: "old-tag" },    // Remove from array
    $unset: { temporary: "" }      // Remove field
  }
)`,
          note: "Different methods and operators for updating documents",
        },
        {
          title: "Delete",
          content: "Remove documents from collections",
          code: `// Delete one document
db.users.deleteOne({ email: "john@example.com" })

// Delete multiple documents
db.users.deleteMany({ age: { $lt: 18 } })

// Delete all documents
db.users.deleteMany({})`,
          note: "Methods to remove documents from collections",
        },
      ],
    },
    {
      title: "Aggregation Pipeline",
      items: [
        {
          title: "Basic Aggregation",
          content: "Common aggregation operations",
          code: `// Basic aggregation pipeline
db.orders.aggregate([
  // Match stage - filter documents
  { $match: { status: "completed" } },
  
  // Group stage - group and calculate
  { $group: {
    _id: "$customerId",
    totalSpent: { $sum: "$amount" },
    avgOrder: { $avg: "$amount" },
    orders: { $push: "$_id" }
  }},
  
  // Sort stage
  { $sort: { totalSpent: -1 } },
  
  // Limit results
  { $limit: 5 }
])

// Lookup (JOIN) example
db.orders.aggregate([
  {
    $lookup: {
      from: "users",
      localField: "userId",
      foreignField: "_id",
      as: "userDetails"
    }
  }
])

// Project stage with calculations
db.sales.aggregate([
  {
    $project: {
      item: 1,
      discount: { $multiply: ["$price", 0.1] },
      finalPrice: { $subtract: ["$price", { $multiply: ["$price", 0.1] }] }
    }
  }
])`,
          note: "Aggregation pipeline for complex data processing and analysis",
        },
      ],
    },
    {
      title: "Indexes",
      items: [
        {
          title: "Index Management",
          content: "Create and manage indexes",
          code: `// Create single field index
db.users.createIndex({ email: 1 })  // 1 for ascending, -1 for descending

// Create compound index
db.users.createIndex({ 
  email: 1, 
  username: -1 
})

// Create unique index
db.users.createIndex(
  { email: 1 },
  { unique: true }
)

// Create text index
db.articles.createIndex(
  { title: "text", content: "text" }
)

// List all indexes
db.users.getIndexes()

// Drop specific index
db.users.dropIndex("email_1")

// Drop all indexes
db.users.dropIndexes()`,
          note: "Index types and management for query optimization",
        },
      ],
    },
    {
      title: "Schema Validation",
      items: [
        {
          title: "JSON Schema Validation",
          content: "Define and enforce document structure",
          code: `// Create collection with schema validation
db.createCollection("users", {
  validator: {
    $jsonSchema: {
      bsonType: "object",
      required: ["name", "email", "age"],
      properties: {
        name: {
          bsonType: "string",
          description: "must be a string and is required"
        },
        email: {
          bsonType: "string",
          pattern: "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$",
          description: "must be a valid email address"
        },
        age: {
          bsonType: "int",
          minimum: 0,
          maximum: 120,
          description: "must be an integer between 0 and 120"
        },
        phone: {
          bsonType: "string",
          description: "must be a string if provided"
        }
      }
    }
  },
  validationLevel: "strict",
  validationAction: "error"
})`,
          note: "Schema validation ensures data consistency and integrity",
        },
      ],
    },
    {
      title: "Security & Administration",
      items: [
        {
          title: "User Management",
          content: "Manage database users and roles",
          code: `// Create user
db.createUser({
  user: "appUser",
  pwd: "secure_password",
  roles: [
    { role: "readWrite", db: "mydatabase" },
    { role: "read", db: "analytics" }
  ]
})

// Grant roles to user
db.grantRolesToUser("appUser", [
  { role: "readWrite", db: "newdb" }
])

// Show current user roles
db.getUser("appUser")

// Remove user
db.dropUser("appUser")

// Change user password
db.changeUserPassword("appUser", "new_secure_password")`,
          note: "User management and access control in MongoDB",
        },
        {
          title: "Backup & Restore",
          content: "Database backup and restoration",
          code: `// Backup database (from terminal)
mongodump --db=mydatabase --out=/backup/path

// Backup collection
mongodump --db=mydatabase --collection=users --out=/backup/path

// Restore database
mongorestore --db=mydatabase /backup/path/mydatabase

// Restore collection
mongorestore --db=mydatabase --collection=users /backup/path/mydatabase/users.bson

// Export to JSON
mongoexport --db=mydatabase --collection=users --out=users.json

// Import from JSON
mongoimport --db=mydatabase --collection=users --file=users.json`,
          note: "Commands for backing up and restoring MongoDB databases",
        },
      ],
    },
  ],
};

export default mongodbCheatsheet;
